/**
 * <consent-gated-field>
 *
 * Makes another field required only while a consent checkbox is ticked.
 *
 * WHY THIS EXISTS
 * On a dedicated opt-in page the consent checkbox can simply be `required` —
 * the form has no other purpose. On the Contact Us form it cannot: a customer
 * must be able to send a message without agreeing to receive texts. That leaves
 * a gap — someone ticks "text me" and leaves the phone field blank, and the
 * store records a consent it can never act on. `required` is not settable from
 * HTML or CSS conditionally, so this is the smallest amount of JS that closes it.
 *
 * USAGE
 *   <consent-gated-field data-gates="ContactForm-phone">
 *     <input type="checkbox" class="brand-checkbox" ...>
 *     <label ...>I agree to receive text messages...</label>
 *   </consent-gated-field>
 *
 *   data-gates  (required) — id of the input that becomes required when ticked.
 *
 * Optionally, an element anywhere in the document carrying
 * `data-required-marker="<same id>"` is shown/hidden in step with the state —
 * used for the asterisk on the gated field's label.
 *
 * The element bails silently when its checkbox or target is missing rather than
 * throwing, per rules/javascript.md — a section carrying it can be mounted on a
 * template where the markup differs.
 */
class ConsentGatedField extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');

    const targetId = this.dataset.gates;
    this.target = targetId ? document.getElementById(targetId) : null;
    if (!this.checkbox || !this.target) return;

    this.marker = targetId
      ? document.querySelector('[data-required-marker="' + targetId + '"]')
      : null;

    this.checkbox.addEventListener('change', this.onChange);

    // Sync on connect, not just on change: after a failed submit Shopify
    // re-renders the form and the browser restores the checkbox state, so the
    // gated field must come back required without the user touching it again.
    this.sync();
  }

  disconnectedCallback() {
    if (this.checkbox) this.checkbox.removeEventListener('change', this.onChange);
  }

  onChange() {
    this.sync();
  }

  sync() {
    const on = this.checkbox.checked;
    this.target.required = on;
    this.target.setAttribute('aria-required', on ? 'true' : 'false');
    if (this.marker) this.marker.hidden = !on;
  }
}

if (!customElements.get('consent-gated-field')) {
  customElements.define('consent-gated-field', ConsentGatedField);
}
